
import { defineComponent, ref, reactive, toRefs, computed, nextTick, WritableComputedRef } from 'vue'
import { SearchOutlined, CloseCircleFilled, DeleteOutlined } from '@ant-design/icons-vue'
import nineDirections from '@/api/nineDirections'
import router from '@/router/index'
import { useRoute } from 'vue-router'
import { gotoClientLogin } from '@/utils/stock'
const SEARCH_DEBOUNCE = 300
const HISTORY_MAX = 10
const API_CONFIG = {
  appcode: 'com.rjhy.uranus',
  serverId: '200',
  appVersion: '3.15.2',
  clientType: 'ios',
  softName: 'jfztadv'
}

interface pageDataType {
  value: string
  options: any[]
  historyData: any[]
  activeLi?: WritableComputedRef<string>
  activeindex: number
  showDropdown: boolean
  selectOption: any
  errorRequest: boolean
  [propName: string]: any
}
interface EventType extends Event {
  code?: string
}
export default defineComponent({
  name: 'SearchCourse',
  components: {
    SearchOutlined,
    CloseCircleFilled,
    DeleteOutlined
  },
  props: {
    token: {
      type: String,
      default: () => {
        return ''
      }
    },
    theme: {
      type: String,
      default: () => {
        return 'light'
      }
    }
  },
  setup(props: any, content: any) {
    const route = useRoute()
    const pageData: pageDataType = reactive({
      value: '',
      options: [],
      historyData: [],
      activeindex: 0,
      showDropdown: false,
      selectOption: [],
      errorRequest: false
    })
    const askingselect = ref()
    const scrollWrapper = ref()
    const searchWrap = ref<HTMLElement | null>(null)
    pageData.activeLi = computed(() => {
      const item = pageData.selectOption[pageData.activeindex]
      return item?.Id?.toString() ?? ''
    })
    pageData.selectOption = computed(() => {
      if (!pageData.value.length && pageData.historyData.length) {
        return [...pageData.historyData].reverse()
      }
      return pageData.options || []
    })

    function isFocusInsideSearch() {
      const root = searchWrap.value
      const active = document.activeElement
      return !!(root && active && root.contains(active))
    }
    
    function handleChange(e: EventType, value: any) {
      preventBlur()
      if (value.type && value.type === 'notCourseGroup') {
        // 历史记录，非课程且非专题
        pageData.value = value.Id
        searchValue()
        handleHistory(value.Id)
      } else {
        handleHistory(value.Id)
        jumpToDetail(value)
        pageData.value = ''
        searchValue()
        blurSelect(1)
        askingselect.value?.blur()
      }
      e.stopPropagation()
      return false
    }
    let searchTimer: any = null
    function searchValue() {
      if (searchTimer) clearTimeout(searchTimer)
      pageData.activeindex = 0
      // 输入过程中兜底保持下拉展开（Win7 误触发 blur 后仍在输入时可回显）
      if (isFocusInsideSearch() || pageData.showDropdown) {
        pageData.showDropdown = true
      }
      nextTick(() => {
        if (scrollWrapper.value) {
          scrollWrapper.value.scrollTop = 0
        }
      })
      if (!pageData.value.trim() || pageData.errorRequest) {
        pageData.options = []
        searchTimer = null
        return
      }
      const keyword = pageData.value.trim()
      searchTimer = setTimeout(() => {
        nineDirections.stockEntrance(
          { types: 'coursePackage', keyword },
          { ...API_CONFIG, token: (route.query.token as string) || '' }
        ).then((res: any) => {
        if (res?.code === 1 && res?.data?.coursePackage) {
          pageData.errorRequest = false
          pageData.options = res.data.coursePackage.filter((item: any) => !item.data?.isVipCourse).map((item: any) => ({
            Id: item.data?.courseNo,
            courseName: item.data?.courseName,
            courseNo: item.data?.courseNo,
            ...item.data
          }))
        } else {
          pageData.options = []
        }
        // 结果返回时若输入框仍聚焦，强制保持下拉可见
        if (isFocusInsideSearch()) {
          pageData.showDropdown = true
        }
      }).catch(() => {
        pageData.errorRequest = true
        pageData.options = []
        if (isFocusInsideSearch()) {
          pageData.showDropdown = true
        }
      })
      }, SEARCH_DEBOUNCE)
    }

    function focusSelect() {
      // 必须清除 pending blur，否则 Win7 上 blur→focus 抖动后定时器仍会把下拉关掉
      preventBlur()
      pageData.showDropdown = true
      window.addEventListener('keydown', handlekeyFn, true)
      const history = JSON.parse(sessionStorage.getItem('jfSchoolHistory') || '[]') || []
      pageData.historyData = history.slice(-HISTORY_MAX)
    }
    let blurTimer: ReturnType<typeof setTimeout> | null = null
    function blurSelect(delay?: number) {
      if (blurTimer) clearTimeout(blurTimer)
      blurTimer = setTimeout(() => {
        // 焦点仍在搜索组件内（含 clear 图标等）时不关闭
        if (isFocusInsideSearch()) {
          blurTimer = null
          return
        }
        pageData.showDropdown = false
        window.removeEventListener('keydown', handlekeyFn, true)
        blurTimer = null
      }, delay ?? 200)
    }
    function enterSelect() {
      if (!pageData.activeLi && !pageData.value.length) return
      if (pageData.errorRequest) {
        reRequest()
        return
      }
      const value = pageData.value.trim()
      if ((pageData.activeLi || '').toString() !== '') {
        const currentItem = pageData.selectOption.find((item: any) => item.Id === pageData.activeLi)
        if (!currentItem) return
        handleHistory(pageData.activeLi)
        if (currentItem.type === 'notCourseGroup') {
          pageData.value = currentItem.Id
          searchValue()
        } else {
          jumpToDetail(currentItem)
          pageData.value = ''
          searchValue()
          blurSelect(1)
          askingselect.value?.blur()
        }
      }
    }
    function jumpToDetail(currentItem: any) {
      if (currentItem.courseNo) {
        const query = JSON.parse(JSON.stringify(route.query))
        delete query.groupCode
        if (!route.query.token) {
          gotoClientLogin()
          return
        }
        router.push({
          name: 'coursedetail',
          query: {
            ...query,
            courseNo: currentItem.courseNo,
            lessonNo: null,
            courseType: currentItem.courseType
          },
          params: {
            nogroupName: 'true'
          }
        })
      } else if (currentItem.groupCode) {
        const query = JSON.parse(JSON.stringify(route.query))
        delete query.courseNo
        router.push({
          name: 'specialcourse',
          query: {
            ...query,
            groupCode: currentItem.groupCode,
            groupName: currentItem.groupName
          }
        })
      }
    }
    function handleHistory(id: any) {
      const history = pageData.historyData.filter((item: any) => item.Id !== id)
      const currentItem = pageData.selectOption.find((item: any) => item.Id === id)
      if (currentItem) {
        history.push(currentItem)
      } else {
        history.push({
          groupName: id,
          Id: id,
          type: 'notCourseGroup'
        })
      }
      if (history.length > HISTORY_MAX) {
        history.shift()
      }
      sessionStorage.setItem('jfSchoolHistory', JSON.stringify(history))
      pageData.historyData = history
    }
    function clearHistory() {
      pageData.historyData = []
      sessionStorage.removeItem('jfSchoolHistory')
    }
    function cleartagsCall() {
      pageData.value = ''
      searchValue()
      preventBlur()
    }
    function cancelArrowUp(e: EventType) {
      if (e.code === 'ArrowUp') {
        e.preventDefault()
        return false
      }
    }
    function handlekeyFn(e: EventType) {
      if (e.code !== 'ArrowDown' && e.code !== 'ArrowUp' && e.code !== 'Enter') return
      // 向下箭头
      if (e.code === 'ArrowDown') {
        pageData.activeindex++
        if (pageData.activeindex >= pageData.selectOption.length) {
          pageData.activeindex = pageData.selectOption.length - 1
        }
      } else if (e.code === 'ArrowUp') {
        pageData.activeindex--
        if (pageData.activeindex < 0) {
          pageData.activeindex = 0
        }
      } else if (e.code === 'Enter') {
        enterSelect()
      }
      nextTick(() => {
        if (pageData.activeindex < 7) {
          if (scrollWrapper.value) {
            scrollWrapper.value.scrollTop = 0
          }
        } else {
          if (scrollWrapper.value) {
            scrollWrapper.value.scrollTop = pageData.activeindex * 24 - 140
          }
        }
      })
    }
    function inputKeyup(e: EventType) {
      if (e.code === 'Enter') {
        enterSelect()
      }
    }
    function reRequest() {
      pageData.errorRequest = false
      searchValue()
    }
    function preventBlur() {
      if (blurTimer) {
        clearTimeout(blurTimer)
        blurTimer = null
      }
    }
    function highlightKeywords(str: string, key?: string) {
      if (!key) return str
      const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const match = str.match(new RegExp(escaped, 'i'))?.[0]
      return match ? str.replace(match, `<font style="color: #ff5c5c">${match}</font>`) : str
    }

    return {
      ...toRefs(pageData),
      handleChange,
      searchValue,
      focusSelect,
      clearHistory,
      blurSelect,
      cleartagsCall,
      askingselect,
      searchWrap,
      enterSelect,
      handlekeyFn,
      inputKeyup,
      scrollWrapper,
      reRequest,
      preventBlur,
      cancelArrowUp,
      highlightKeywords
    }
  }
})
